import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage, inr, todayISO, formatDate } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Undo, RotateCw, RefreshCw, ClipboardList } from "lucide-react";
import MedicineTypeahead from "@/components/MedicineTypeahead";

export default function SalesReturns() {
  const [returns, setReturns] = useState([]);
  const [bills, setBills] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [selectedBillId, setSelectedBillId] = useState("");
  const [selectedBill, setSelectedBill] = useState(null);
  const [returnMode, setReturnMode] = useState("Single Item Return");
  const [selectedItemIdx, setSelectedItemIdx] = useState("");
  const [returnUnit, setReturnUnit] = useState("");
  const [returnQty, setReturnQty] = useState(1);
  const [returnType, setReturnType] = useState("Refund"); // "Refund" or "Exchange"
  const [reason, setReason] = useState("");
  const [returnDate, setReturnDate] = useState(todayISO());

  // Exchange replacement states
  const [replacementMedicine, setReplacementMedicine] = useState(null);
  const [replacementBatches, setReplacementBatches] = useState([]);
  const [selectedReplacementBatchNo, setSelectedReplacementBatchNo] = useState("");
  const [selectedReplacementBatch, setSelectedReplacementBatch] = useState(null);
  const [replacementQty, setReplacementQty] = useState(1);
  const [loadingReplacementBatches, setLoadingReplacementBatches] = useState(false);

  const loadData = async () => {
    try {
      const [rRes, bRes, mRes] = await Promise.all([
        api.get("/sales-returns"),
        api.get("/bills", { params: { limit: 200 } }),
        api.get("/medicines", { params: { limit: 500 } }),
      ]);
      setReturns(rRes.data);
      setBills(bRes.data);
      setMedicines(mRes.data);
    } catch (err) {
      toast.error("Failed to load sales returns data");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getMedUnits = (med, item) => {
    const packingUnit = med?.packing_unit || med?.unit || item?.selling_unit || "Strip";
    const baseUnit = med?.base_unit || "Tablet";
    const unitsPerPack = Math.max(1, Number(med?.units_per_pack || 10));
    let allowedUnits = med?.allowed_selling_units;
    if (!Array.isArray(allowedUnits) || allowedUnits.length === 0) {
      allowedUnits = packingUnit === baseUnit ? [packingUnit] : [packingUnit, baseUnit];
    }
    return { packingUnit, baseUnit, unitsPerPack, allowedUnits };
  };

  const formatQuantityBreakdown = (baseQty, packingUnit, baseUnit, unitsPerPack) => {
    const qty = Number(baseQty || 0);
    if (packingUnit === baseUnit || unitsPerPack <= 1) {
      return `${qty} ${baseUnit}${qty === 1 ? "" : "s"}`;
    }
    const packs = Math.floor(qty / unitsPerPack);
    const loose = qty % unitsPerPack;
    if (packs > 0 && loose > 0) {
      return `${qty} ${baseUnit}s (${packs} ${packingUnit}${packs > 1 ? "s" : ""} + ${loose} ${baseUnit}${loose > 1 ? "s" : ""})`;
    } else if (packs > 0 && loose === 0) {
      return `${qty} ${baseUnit}s (${packs} ${packingUnit}${packs > 1 ? "s" : ""})`;
    }
    return `${qty} ${baseUnit}${qty === 1 ? "" : "s"}`;
  };

  const getItemSoldBaseQty = (item, med) => {
    if (item?.converted_base_quantity != null && Number(item.converted_base_quantity) > 0) {
      return Math.round(Number(item.converted_base_quantity));
    }
    const { packingUnit, baseUnit, unitsPerPack } = getMedUnits(med, item);
    const qty = Number(item?.entered_quantity ?? item?.quantity ?? 0);
    const sellingUnit = item?.selling_unit || packingUnit;
    const isBase = sellingUnit === baseUnit && packingUnit !== baseUnit;
    return Math.round(isBase ? qty : qty * unitsPerPack);
  };

  const getItemAlreadyReturnedBaseQty = (bill, item, med) => {
    if (!bill || !item) return 0;
    let totalReturnedBase = 0;
    const billReturns = returns.filter((r) => r.bill_id === bill.id);
    for (const r of billReturns) {
      const rItems = r.return_items || [];
      if (rItems.length > 0) {
        for (const ri of rItems) {
          const match = (item.batch_id && ri.batch_id === item.batch_id)
            || (ri.medicine_id === item.medicine_id && (ri.batch_no || "") === (item.batch_no || ""));
          if (match) {
            if (ri.returned_base_quantity != null) {
              totalReturnedBase += Number(ri.returned_base_quantity || 0);
            } else {
              const { packingUnit, baseUnit, unitsPerPack } = getMedUnits(med, item);
              const rUnit = ri.return_unit || item.selling_unit || packingUnit;
              const isBase = rUnit === baseUnit && packingUnit !== baseUnit;
              const entered = Number(ri.returned_quantity || 0);
              totalReturnedBase += Math.round(isBase ? entered : entered * unitsPerPack);
            }
          }
        }
      } else {
        const matchMedId = r.original_medicine_id || r.medicine_id;
        const matchBatchNo = r.original_batch_no ?? r.batch_no ?? "";
        const matchBatchId = r.original_batch_id || r.batch_id;
        const match = (item.batch_id && matchBatchId === item.batch_id)
          || (matchMedId === item.medicine_id && matchBatchNo === (item.batch_no || ""));
        if (match) {
          if (r.returned_base_quantity != null) {
            totalReturnedBase += Number(r.returned_base_quantity || 0);
          } else {
            const { packingUnit, baseUnit, unitsPerPack } = getMedUnits(med, item);
            const rUnit = r.return_unit || item.selling_unit || packingUnit;
            const isBase = rUnit === baseUnit && packingUnit !== baseUnit;
            const entered = Number(r.returned_quantity || r.return_quantity || 0);
            totalReturnedBase += Math.round(isBase ? entered : entered * unitsPerPack);
          }
        }
      }
    }
    return totalReturnedBase;
  };

  const getItemRemainingBaseQty = (bill, item, med) => {
    const soldBase = getItemSoldBaseQty(item, med);
    const returnedBase = getItemAlreadyReturnedBaseQty(bill, item, med);
    return Math.max(0, soldBase - returnedBase);
  };

  const hasRemainingReturnableQty = (bill) =>
    (bill?.items || []).some((item) => {
      const med = medicines.find((m) => m.id === item.medicine_id);
      return getItemRemainingBaseQty(bill, item, med) > 0;
    });

  const returnableBills = bills.filter(hasRemainingReturnableQty);

  const getSingleReturnItems = () => {
    if (!selectedBill) return [];
    return selectedBill.items.map((item, idx) => {
      const med = medicines.find((m) => m.id === item.medicine_id);
      const uinfo = getMedUnits(med, item);
      const soldBase = getItemSoldBaseQty(item, med);
      const returnedBase = getItemAlreadyReturnedBaseQty(selectedBill, item, med);
      const remainingBase = Math.max(0, soldBase - returnedBase);
      return {
        ...item,
        originalIndex: idx,
        uinfo,
        soldBase,
        returnedBase,
        remainingBase,
      };
    }).filter((item) => item.remainingBase > 0);
  };

  const getFullBillReturnItems = () => {
    if (!selectedBill) return [];
    return selectedBill.items.map((item) => {
      const med = medicines.find((m) => m.id === item.medicine_id);
      const uinfo = getMedUnits(med, item);
      const soldBase = getItemSoldBaseQty(item, med);
      const returnedBase = getItemAlreadyReturnedBaseQty(selectedBill, item, med);
      const remainingBase = Math.max(0, soldBase - returnedBase);
      return {
        ...item,
        uinfo,
        soldBase,
        returnedBase,
        remainingBase,
      };
    }).filter((item) => item.remainingBase > 0);
  };

  const getFullBillRefundAmount = () => {
    if (!selectedBill) return 0;
    const returnItems = getFullBillReturnItems();
    const sumLineNets = selectedBill.items.reduce((sum, it) => {
      const lineGross = Number(it.mrp || 0) * Number(it.quantity || 0);
      const lineDisc = lineGross * (Number(it.discount_percent || 0) / 100);
      return sum + (lineGross - lineDisc);
    }, 0);
    return Number(returnItems.reduce((sum, it) => {
      const lineGross = Number(it.mrp || 0) * Number(it.quantity || 0);
      const lineDisc = lineGross * (Number(it.discount_percent || 0) / 100);
      const lineNet = lineGross - lineDisc;
      const lineShare = sumLineNets > 0 ? (lineNet / sumLineNets) * selectedBill.grand_total : lineNet;
      const fraction = it.soldBase > 0 ? it.remainingBase / it.soldBase : 0;
      return sum + (lineShare * fraction);
    }, 0).toFixed(2));
  };

  const convertReturnQtyToBase = (qty, unit, uinfo) => {
    const q = Number(qty || 0);
    if (unit === uinfo.packingUnit && uinfo.packingUnit !== uinfo.baseUnit) {
      return Math.round(q * uinfo.unitsPerPack);
    }
    return Math.round(q);
  };

  const handleBillChange = (billId) => {
    setSelectedBillId(billId);
    const bill = bills.find((b) => b.id === billId);
    setSelectedBill(bill || null);
    setSelectedItemIdx("");
    setReturnUnit("");
    setReturnQty(1);
  };

  const handleItemChange = (idxString) => {
    setSelectedItemIdx(idxString);
    if (idxString === "") {
      setReturnQty(1);
      setReturnUnit("");
      return;
    }
    const item = selectedBill?.items[parseInt(idxString, 10)];
    if (!item) return;
    const med = medicines.find((m) => m.id === item.medicine_id);
    const uinfo = getMedUnits(med, item);
    const remainingBase = getItemRemainingBaseQty(selectedBill, item, med);
    let defUnit = uinfo.allowedUnits.includes(item.selling_unit) ? item.selling_unit : uinfo.allowedUnits[0];
    if (
      defUnit === uinfo.packingUnit &&
      uinfo.packingUnit !== uinfo.baseUnit &&
      remainingBase < uinfo.unitsPerPack &&
      uinfo.allowedUnits.includes(uinfo.baseUnit)
    ) {
      defUnit = uinfo.baseUnit;
    }
    setReturnUnit(defUnit);
    setReturnQty(1);
  };

  useEffect(() => {
    if (!selectedBill || selectedItemIdx === "") return;
    const item = selectedBill.items[parseInt(selectedItemIdx, 10)];
    const med = medicines.find((m) => m.id === item?.medicine_id);
    if (!item || getItemRemainingBaseQty(selectedBill, item, med) <= 0) {
      setSelectedItemIdx("");
      setReturnUnit("");
      setReturnQty(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBill, selectedItemIdx, returns]);

  // When replacement medicine is picked
  const handleReplacementSelect = async (med) => {
    setReplacementMedicine(med);
    setSelectedReplacementBatchNo("");
    setSelectedReplacementBatch(null);
    setReplacementQty(1);
    setReplacementBatches([]);

    if (!med) return;
    setLoadingReplacementBatches(true);
    try {
      const { data } = await api.get(`/medicines/${med.id}/batches`);
      const activeBatches = (data.batches || []).filter((b) => b.stock > 0);
      setReplacementBatches(activeBatches);
    } catch (err) {
      toast.error("Failed to fetch replacement batches");
    } finally {
      setLoadingReplacementBatches(false);
    }
  };

  const handleReplacementBatchChange = (batchNo) => {
    setSelectedReplacementBatchNo(batchNo);
    const batch = replacementBatches.find((b) => b.batch_no === batchNo);
    setSelectedReplacementBatch(batch || null);
    setReplacementQty(1);
  };

  // Calculate live values
  const getCalculatedValues = () => {
    if (!selectedBill || selectedItemIdx === "") {
      return { refundableAmount: 0, replacementCost: 0, difference: 0, convertedReturnedBase: 0 };
    }
    const item = selectedBill.items[parseInt(selectedItemIdx, 10)];
    if (!item) return { refundableAmount: 0, replacementCost: 0, difference: 0, convertedReturnedBase: 0 };
    const med = medicines.find((m) => m.id === item.medicine_id);
    const uinfo = getMedUnits(med, item);
    const soldBase = getItemSoldBaseQty(item, med);
    const activeUnit = returnUnit || item.selling_unit || uinfo.packingUnit;
    const convertedReturnedBase = convertReturnQtyToBase(returnQty, activeUnit, uinfo);

    const sumLineNets = selectedBill.items.reduce((sum, it) => {
      const lineGross = Number(it.mrp || 0) * Number(it.quantity || 0);
      const lineDisc = lineGross * (Number(it.discount_percent || 0) / 100);
      return sum + (lineGross - lineDisc);
    }, 0);

    const lineGross = Number(item.mrp || 0) * Number(item.quantity || 0);
    const lineDisc = lineGross * (Number(item.discount_percent || 0) / 100);
    const lineNet = lineGross - lineDisc;
    const lineShare = sumLineNets > 0 ? (lineNet / sumLineNets) * selectedBill.grand_total : lineNet;

    const fraction = soldBase > 0 ? convertedReturnedBase / soldBase : 0;
    const refundableAmount = Number((lineShare * fraction).toFixed(2));

    let replacementCost = 0;
    if (returnType === "Exchange") {
      const itemMrp = selectedReplacementBatch
        ? selectedReplacementBatch.mrp
        : (replacementMedicine ? replacementMedicine.mrp : 0);
      replacementCost = Number((itemMrp * replacementQty).toFixed(2));
    }

    const difference = Number((refundableAmount - replacementCost).toFixed(2));

    return { refundableAmount, replacementCost, difference, convertedReturnedBase };
  };

  const handleSave = async () => {
    if (!selectedBillId) return toast.error("Please select a bill");
    if (returnMode === "Full Bill Return") {
      const returnItems = getFullBillReturnItems();
      if (returnItems.length === 0) return toast.error("All items in this bill have already been returned");
      setSubmitting(true);
      try {
        await api.post("/sales-returns", {
          bill_id: selectedBillId,
          return_mode: "Full Bill Return",
          return_type: "Refund",
          return_reason: reason,
          return_date: returnDate,
        });
        toast.success("Full bill return processed successfully");
        setOpen(false);
        resetForm();
        await loadData();
      } catch (err) {
        toast.error(getApiErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (selectedItemIdx === "") return toast.error("Please select a billed medicine");
    const item = selectedBill.items[parseInt(selectedItemIdx, 10)];
    const med = medicines.find((m) => m.id === item.medicine_id);
    const uinfo = getMedUnits(med, item);
    const remainingBase = getItemRemainingBaseQty(selectedBill, item, med);
    const activeUnit = returnUnit || item.selling_unit || uinfo.packingUnit;

    const qtyNum = Number(returnQty);
    if (!returnQty || isNaN(qtyNum) || qtyNum <= 0) {
      return toast.error("Return quantity must be greater than 0");
    }

    if (activeUnit === uinfo.packingUnit && uinfo.packingUnit !== uinfo.baseUnit) {
      if (!Number.isInteger(qtyNum)) {
        return toast.error(`Return quantity for ${uinfo.packingUnit} must be a whole number`);
      }
    } else if (activeUnit === uinfo.baseUnit) {
      if (!Number.isInteger(qtyNum)) {
        return toast.error(`Return quantity for ${uinfo.baseUnit} must be a whole number`);
      }
    }

    const convertedReturnedBase = convertReturnQtyToBase(qtyNum, activeUnit, uinfo);
    if (convertedReturnedBase <= 0) {
      return toast.error("Return quantity must be greater than 0");
    }
    if (convertedReturnedBase > remainingBase) {
      return toast.error(
        `Return quantity (${qtyNum} ${activeUnit} = ${convertedReturnedBase} ${uinfo.baseUnit}) exceeds remaining returnable quantity (${remainingBase} ${uinfo.baseUnit})`
      );
    }

    if (returnType === "Exchange") {
      if (!replacementMedicine) return toast.error("Please select a replacement medicine");
      if (replacementBatches.length > 0 && !selectedReplacementBatchNo) {
        return toast.error("Please select a replacement batch");
      }
      if (replacementQty <= 0) return toast.error("Replacement quantity must be greater than 0");

      const availableStock = selectedReplacementBatch
        ? selectedReplacementBatch.stock
        : (replacementMedicine ? replacementMedicine.stock : 0);

      if (replacementQty > availableStock) {
        return toast.error(`Replacement quantity cannot exceed available stock (${availableStock})`);
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        bill_id: selectedBillId,
        return_mode: "Single Item Return",
        medicine_id: item.medicine_id,
        batch_no: item.batch_no || "",
        batch_id: item.batch_id || null,
        item_index: parseInt(selectedItemIdx, 10),
        quantity: Number(returnQty),
        return_quantity: Number(returnQty),
        return_unit: activeUnit,
        return_type: returnType,
        return_reason: reason,
        return_date: returnDate,
        replacement_medicine_id: returnType === "Exchange" ? replacementMedicine.id : null,
        replacement_batch_no: returnType === "Exchange" ? (selectedReplacementBatchNo || replacementMedicine.id) : null,
        replacement_quantity: returnType === "Exchange" ? Number(replacementQty) : null,
      };

      await api.post("/sales-returns", payload);
      toast.success("Sales return processed successfully");
      setOpen(false);
      resetForm();
      await loadData();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedBillId("");
    setSelectedBill(null);
    setReturnMode("Single Item Return");
    setSelectedItemIdx("");
    setReturnUnit("");
    setReturnQty(1);
    setReturnType("Refund");
    setReason("");
    setReturnDate(todayISO());
    setReplacementMedicine(null);
    setReplacementBatches([]);
    setSelectedReplacementBatchNo("");
    setSelectedReplacementBatch(null);
    setReplacementQty(1);
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  const { refundableAmount, replacementCost, difference, convertedReturnedBase } = getCalculatedValues();
  const selectedOriginalItem = selectedBill ? selectedBill.items[parseInt(selectedItemIdx, 10)] : null;
  const selectedItemMed = selectedOriginalItem ? medicines.find((m) => m.id === selectedOriginalItem.medicine_id) : null;
  const selectedItemUInfo = selectedOriginalItem ? getMedUnits(selectedItemMed, selectedOriginalItem) : null;
  const selectedItemSoldBase = selectedOriginalItem ? getItemSoldBaseQty(selectedOriginalItem, selectedItemMed) : 0;
  const selectedItemReturnedBase = selectedOriginalItem ? getItemAlreadyReturnedBaseQty(selectedBill, selectedOriginalItem, selectedItemMed) : 0;
  const selectedItemRemainingBase = selectedOriginalItem ? getItemRemainingBaseQty(selectedBill, selectedOriginalItem, selectedItemMed) : 0;
  const selectedItem = selectedOriginalItem && selectedItemRemainingBase > 0 ? selectedOriginalItem : null;
  const singleReturnItems = getSingleReturnItems();
  const fullBillReturnItems = getFullBillReturnItems();
  const fullBillRefundAmount = getFullBillRefundAmount();

  return (
    <div className="space-y-6" data-testid="sales-returns-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Transactions</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Sales Returns</h1>
        </div>
        <Button onClick={openNew} data-testid="add-sales-return-btn">
          <Plus size={16} className="mr-1" /> Process Return
        </Button>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Return Date</TableHead>
              <TableHead>Bill No</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Returned Med (Batch)</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Replacement Med (Batch)</TableHead>
              <TableHead className="text-right">Refund / Diff</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {returns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  <ClipboardList className="mx-auto mb-2 opacity-40" size={24} />
                  No sales returns processed yet.
                </TableCell>
              </TableRow>
            ) : (
              returns.map((r) => (
                <TableRow key={r.id} data-testid={`sales-return-row-${r.id}`}>
                  <TableCell className="text-sm">{formatDate(r.return_date)}</TableCell>
                  <TableCell className="text-sm font-semibold">{r.bill_no}</TableCell>
                  <TableCell className="text-sm">
                    <div>{r.customer_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.customer_phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{r.original_medicine_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">Batch: {r.original_batch_no || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm text-right tabular-nums font-semibold">
                    {r.return_quantity || r.returned_quantity} {r.return_unit || ""}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${r.return_type === "Refund"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}
                    >
                      {r.return_mode === "Full Bill Return" ? "Full Refund" : r.return_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.return_type === "Exchange" ? (
                      <>
                        <div className="font-medium">{r.replacement_medicine_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          Batch: {r.replacement_batch_no || "—"} (x{r.replacement_quantity})
                        </div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right tabular-nums font-bold">
                    {r.return_type === "Refund" ? (
                      <span className="text-red-600">-{inr(r.refund_amount)}</span>
                    ) : r.price_difference > 0 ? (
                      <span className="text-red-600">Refund: -{inr(r.price_difference)}</span>
                    ) : r.price_difference < 0 ? (
                      <span className="text-green-600">Collect: +{inr(Math.abs(r.price_difference))}</span>
                    ) : (
                      <span className="text-muted-foreground">₹0.00</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.return_reason || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Return Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="sales-return-dialog">
          <DialogHeader>
            <DialogTitle>Process Sales Return</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 my-2 max-h-[70vh] overflow-y-auto px-1">
            {/* Bill Selection */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Select Customer Bill *</Label>
              <Select value={selectedBillId} onValueChange={handleBillChange}>
                <SelectTrigger data-testid="select-bill">
                  <SelectValue placeholder="Select Bill Number" />
                </SelectTrigger>
                <SelectContent>
                  {returnableBills.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.bill_no} - {b.customer_name} ({b.customer_phone || "No Phone"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedBill && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Return Mode *</Label>
                <Select value={returnMode} onValueChange={(v) => { setReturnMode(v); setSelectedItemIdx(""); setReturnType("Refund"); }}>
                  <SelectTrigger data-testid="select-return-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single Item Return">Single Item Return</SelectItem>
                    <SelectItem value="Full Bill Return">Full Bill Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Billed Item Selection */}
            {selectedBill && returnMode === "Single Item Return" && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Returned Item & Batch *</Label>
                <Select value={selectedItemIdx} onValueChange={handleItemChange}>
                  <SelectTrigger data-testid="select-billed-item">
                    <SelectValue placeholder="Select Item to Return" />
                  </SelectTrigger>
                  <SelectContent>
                    {singleReturnItems.map((it) => (
                      <SelectItem key={it.originalIndex} value={String(it.originalIndex)}>
                        {it.medicine_name} (Batch: {it.batch_no || "No Batch"}) Remaining: {formatQuantityBreakdown(it.remainingBase, it.uinfo.packingUnit, it.uinfo.baseUnit, it.uinfo.unitsPerPack)} @ {inr(it.mrp)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sold & Remaining Qty Details */}
            {selectedBill && returnMode === "Full Bill Return" && (
              <div className="p-3 bg-muted/40 rounded border border-border space-y-2 text-sm">
                <div className="font-semibold">Full Bill Return Items</div>
                {fullBillReturnItems.length === 0 ? <div className="text-destructive text-xs">All items have already been returned.</div> : fullBillReturnItems.map((it, idx) => (
                  <div key={idx} className="flex justify-between gap-3 text-xs">
                    <span>{it.medicine_name} <span className="text-muted-foreground">(Batch: {it.batch_no || "—"})</span></span>
                    <span className="font-semibold">
                      Return Qty: {formatQuantityBreakdown(it.remainingBase, it.uinfo.packingUnit, it.uinfo.baseUnit, it.uinfo.unitsPerPack)}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-border pt-2 font-bold">
                  <span>Total Refund</span>
                  <span className="text-red-600">-{inr(fullBillRefundAmount)}</span>
                </div>
              </div>
            )}

            {selectedItem && returnMode === "Single Item Return" && selectedItemUInfo && (
              <div className="p-3 bg-muted/40 rounded border border-border space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Qty Sold</div>
                    <div className="font-semibold text-foreground text-sm">
                      {formatQuantityBreakdown(selectedItemSoldBase, selectedItemUInfo.packingUnit, selectedItemUInfo.baseUnit, selectedItemUInfo.unitsPerPack)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Already Returned</div>
                    <div className="font-semibold text-amber-600 text-sm">
                      {formatQuantityBreakdown(selectedItemReturnedBase, selectedItemUInfo.packingUnit, selectedItemUInfo.baseUnit, selectedItemUInfo.unitsPerPack)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Remaining Returnable</div>
                    <div className="font-semibold text-emerald-600 text-sm">
                      {formatQuantityBreakdown(selectedItemRemainingBase, selectedItemUInfo.packingUnit, selectedItemUInfo.baseUnit, selectedItemUInfo.unitsPerPack)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Qty, Unit & Return Action Type */}
            {selectedItem && returnMode === "Single Item Return" && selectedItemUInfo && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Return Unit *</Label>
                    <Select value={returnUnit || selectedItemUInfo.allowedUnits[0]} onValueChange={setReturnUnit}>
                      <SelectTrigger data-testid="select-return-unit">
                        <SelectValue placeholder="Select Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedItemUInfo.allowedUnits.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Return Qty *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={returnQty}
                      onChange={(e) => setReturnQty(e.target.value)}
                      data-testid="return-qty"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Return Action Type *</Label>
                    <Select value={returnType} onValueChange={setReturnType}>
                      <SelectTrigger data-testid="select-return-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Refund">Refund Money</SelectItem>
                        <SelectItem value="Exchange">Exchange Items</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedItemUInfo.packingUnit !== selectedItemUInfo.baseUnit && (
                  <div className="text-xs text-muted-foreground bg-muted/20 px-3 py-1.5 rounded border border-border/50 flex justify-between">
                    <span>1 {selectedItemUInfo.packingUnit} = {selectedItemUInfo.unitsPerPack} {selectedItemUInfo.baseUnit}s</span>
                    <span className="font-medium text-foreground">
                      Converted: {convertedReturnedBase} {selectedItemUInfo.baseUnit}{convertedReturnedBase === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Exchange Section */}
            {returnType === "Exchange" && selectedItem && returnMode === "Single Item Return" && (
              <div className="p-4 bg-muted/20 border border-border rounded-md space-y-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Replacement Item Details</div>

                {/* Replacement Medicine Typeahead */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Search Replacement Medicine *</Label>
                  <MedicineTypeahead
                    value={replacementMedicine?.id || ""}
                    valueLabel={replacementMedicine?.name || ""}
                    medicines={medicines}
                    onSelect={handleReplacementSelect}
                    onAddNew={() => { }}
                    testId="replacement-med-picker"
                  />
                </div>

                {/* Replacement Batch Selector */}
                {replacementMedicine && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Select Active Batch *</Label>
                      {loadingReplacementBatches ? (
                        <div className="text-xs py-2 text-muted-foreground">Loading...</div>
                      ) : replacementBatches.length === 0 ? (
                        <div className="text-xs py-2 text-destructive">No active stock in any batch</div>
                      ) : (
                        <Select value={selectedReplacementBatchNo} onValueChange={handleReplacementBatchChange}>
                          <SelectTrigger data-testid="select-replacement-batch">
                            <SelectValue placeholder="Select Batch" />
                          </SelectTrigger>
                          <SelectContent>
                            {replacementBatches.map((b) => (
                              <SelectItem key={b.batch_no} value={b.batch_no}>
                                {b.batch_no} (Stock: {b.stock} @ {inr(b.mrp)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Replacement Qty *</Label>
                      <Input
                        type="number"
                        min="1"
                        max={selectedReplacementBatch ? selectedReplacementBatch.stock : (replacementMedicine ? replacementMedicine.stock : undefined)}
                        value={replacementQty}
                        onChange={(e) => {
                          const limit = selectedReplacementBatch ? selectedReplacementBatch.stock : (replacementMedicine ? replacementMedicine.stock : 9999);
                          setReplacementQty(Math.min(limit, Math.max(1, parseInt(e.target.value, 10) || 1)));
                        }}
                        data-testid="replacement-qty"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Calculations Panel */}
            {selectedItem && returnMode === "Single Item Return" && (
              <div className="p-4 bg-muted/40 rounded border border-border space-y-2 text-sm tabular-nums">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground font-medium">Refundable Amount:</span>
                  <span className="font-bold text-red-600 text-base">-{inr(refundableAmount)}</span>
                </div>
                {returnType === "Exchange" && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Replacement Cost:</span>
                      <span className="font-semibold text-foreground">+{inr(replacementCost)}</span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2 font-bold text-base">
                      <span>Net Difference:</span>
                      {difference > 0 ? (
                        <span className="text-red-600">Refund Customer: {inr(difference)}</span>
                      ) : difference < 0 ? (
                        <span className="text-green-600">Collect Extra: {inr(Math.abs(difference))}</span>
                      ) : (
                        <span className="text-muted-foreground">₹0.00</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Date & Reason */}
            {(selectedItem || (selectedBill && returnMode === "Full Bill Return")) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Return Date</Label>
                  <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Return Reason</Label>
                  <Input
                    placeholder="e.g. Near expiry, damaged, patient changed mind"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="return-reason"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={submitting} data-testid="save-sales-return">
              <RefreshCw size={14} className="mr-1.5" />
              {submitting ? "Processing..." : "Save Sales Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
